import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-final-form';
import { DragNDropRecExpanded, RecExpandedRow } from 'components/DragNDrop/DragNDropRecExpanded';
import { useFieldValue } from 'hooks/useFormHelpers';
import { AdminFilter } from 'store/clientFilters/types';
import { useSaveFilterOrder } from './useSaveFilterOrder';
import { FILTERS_NO_SEARCH_MATCH, FILTERS_ORDER_HINT } from './constants';
import { haveSameFilterOrder, matchesFilterSearch, toFilterOrderRows } from './utils';
import { trackEvent } from 'helpers/mixpanelEvents';

type Props = {
  clientId: number;
  filters: AdminFilter[];
  isReadOnly: boolean;
  onEditFilter?: (filterId: string) => void;
  renderStatusSwitch: (filterId: string) => ReactNode;
};

/** Saves the new order on every drop and puts the previous one back if the save fails. */
export const FilterOrderList: FC<Props> = ({
  clientId,
  filters,
  isReadOnly,
  onEditFilter,
  renderStatusSwitch,
}) => {
  const { change } = useForm();
  const filterIds: string[] = useFieldValue('filter_ids');
  const saveFilterOrder = useSaveFilterOrder(clientId);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const isSearching = search.trim() !== '';
  const rows = useMemo(() => toFilterOrderRows(filters, filterIds), [filters, filterIds]);
  const visibleRows = useMemo(
    () => (isSearching ? rows.filter(row => matchesFilterSearch(row, search)) : rows),
    [rows, isSearching, search]
  );
  const canDrag = !isReadOnly && !isSearching;

  const saveDroppedOrder = useCallback(
    async (orderedRows: RecExpandedRow[]) => {
      const droppedIds = orderedRows.map(row => row.value);
      if (haveSameFilterOrder({ filter_ids: droppedIds }, { filter_ids: filterIds })) return;
      change('filter_ids', droppedIds);
      setIsSaving(true);
      const isSaved = await saveFilterOrder(droppedIds);
      setIsSaving(false);
      if (isSaved) {
        trackEvent('Filters Reordered', { screen: 'filters' });
      } else {
        change('filter_ids', filterIds);
      }
    },
    [change, filterIds, saveFilterOrder]
  );

  return (
    <>
      <div className="CustomTableHeader client-filters__search-header">
        <span className="oath-bodysmall gray">
          {canDrag ? `Drag to reorder. ${FILTERS_ORDER_HINT}` : FILTERS_ORDER_HINT}
        </span>
        <div className="RightContainer">
          <div className="search-container">
            <input
              type="search"
              placeholder="Search"
              aria-label="Search filters"
              value={search}
              onChange={event => setSearch(event.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>
      <DragNDropRecExpanded
        rows={visibleRows}
        setRows={saveDroppedOrder}
        variant="filter"
        withDetails={false}
        impactScoreVisibility={false}
        disableDelete
        // Off while searching, since a subset has no full order to save, and while a save is in
        // flight, so two orders can't race.
        disableDrag={!canDrag || isSaving}
        onEditRow={onEditFilter}
        renderRowExtra={renderStatusSwitch}
      />
      {visibleRows.length === 0 && isSearching && (
        <span className="oath-bodysmall gray">{FILTERS_NO_SEARCH_MATCH}</span>
      )}
    </>
  );
};
